import type { TrainingFormationDto } from '@ukinfo/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { TRAINING_PLANS_RESOURCE } from '../lib/training';
import { PageHeader } from '../components/PageHeader';
import { FormationDetailDialog } from '../components/training/FormationDetailDialog';
import { FormationFormDialog } from '../components/training/FormationFormDialog';
import { FormationSummary, GameSizeFilterBar, useFormationList, type GameSizeFilter } from '../components/training/FormationList';
import { LinkButton, TestBadge } from '../components/training/common';
import { Button, EmptyState, ErrorBlock, LoadingBlock } from '../components/ui';

/**
 * Formationen: Startaufstellungen fuer die Trainingsplanung.
 *
 * Standardformationen und eigene stehen in einer Liste, nach Spielform filterbar. Die Detailansicht hat
 * eine eigene Adresse (/sport/formationen/:id) - ein Link auf eine Formation bleibt teilbar.
 */
export function FormationsPage(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can(TRAINING_PLANS_RESOURCE, 'update');

  const [gameSize, setGameSize] = useState<GameSizeFilter>('ALL');
  const [form, setForm] = useState<{ formation: TrainingFormationDto | null } | null>(null);
  const list = useFormationList(gameSize);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Formationen"
        description="Startaufstellungen: Standardformationen und eigene für die Trainingsplanung"
        scoped={false}
        actions={
          <>
            <span className="self-center"><TestBadge /></span>
            <LinkButton to="/sport/uebungen">Übungen</LinkButton>
            <LinkButton to="/sport/einheiten">Einheiten</LinkButton>
            {canEdit && <Button onClick={() => setForm({ formation: null })}>+ Neue Formation</Button>}
          </>
        }
      />

      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Die Trainingsplanung läuft als Test. Eine Formation fügst du im Skizzeneditor einer Übung über „Mehr“ → „Formation“ als Spieler in die Skizze ein.
      </p>

      <GameSizeFilterBar value={gameSize} onChange={setGameSize} />

      {list.isLoading && <LoadingBlock />}
      {list.error && <ErrorBlock error={list.error} />}
      {list.data && list.data.length === 0 && <EmptyState title="Keine Formationen gefunden" description="Wähle eine andere Spielform." />}
      {list.data && list.data.length > 0 && (
        <>
          <p className="text-sm text-slate-500" aria-live="polite">{list.data.length} {list.data.length === 1 ? 'Formation' : 'Formationen'}</p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {list.data.map((formation) => (
              <li key={formation.id}>
                <Link
                  to={`/sport/formationen/${formation.id}`}
                  className="flex h-full gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/40"
                >
                  <FormationSummary formation={formation} />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {id && !form && (
        <FormationDetailDialog
          key={id}
          id={id}
          canEdit={canEdit}
          onClose={() => navigate('/sport/formationen')}
          onEdit={(formation) => setForm({ formation })}
          onCopied={(copy) => {
            navigate(`/sport/formationen/${copy.id}`);
            setForm({ formation: copy });
          }}
        />
      )}

      {form && (
        <FormationFormDialog
          formation={form.formation}
          onClose={() => setForm(null)}
          onSaved={(saved) => navigate(`/sport/formationen/${saved.id}`)}
        />
      )}
    </div>
  );
}
